import './SimplePopUp.css';
import * as Common from '@/libs/Common';
import DOMPurify from 'dompurify';

export enum SimplePopUpModalType {
    Info = 'Info',
    Warning = 'Warning',
    Success = 'Success',
    Html = 'Html'
}

export enum SimplePopUpSize {
    sm = 'sm',
    lg = 'lg',
    xl = 'xl',
    full = 'full'
}

export enum SimplePopUpBtnType {
    href = 'href',
    href_blank = 'href_blank',
    callback = 'callback'
}

export interface ISimplePopUpBtnOption<T> {
    id: string;
    type: SimplePopUpBtnType;
    btnClass?: string | 'btn';
    btnDisabled?: boolean;
    resolveParam?: T;
    title: string;
    href?: string;
}

interface ISimplePopUp {
    timerInterval: number;
    showAlert<T>(
        type: SimplePopUpModalType,
        title: string | undefined | null,
        msg: string | undefined | null,
        buttonOptions?: Array<ISimplePopUpBtnOption<T>>,
        modalSize?: SimplePopUpSize,
        preventClose?: boolean,
        didOpen?: (arg?: any) => any,
        timerSec?: number
    ): Promise<any>;

    show(
        html: string | undefined | null,
        buttonOptions?: Array<ISimplePopUpBtnOption<any>>,
        modalSize?: SimplePopUpSize,
        preventClose?: boolean,
        didOpen?: (arg?: any) => any,
        timerSec?: number
    ): Promise<any>;
    hide(): void;
    thread(): Array<any>;
}

export class SimplePopUp implements ISimplePopUp {
    constructor() {
        this.timerInterval = 0;
    }
    timerInterval = 0;
    thread(): Array<any> {
        let resolve, reject;
        const promise = new Promise((res, rej) => {
            resolve = res;
            reject = rej;
        });
        return [promise, resolve, reject];
    }

    showAlert<T>(
        type: SimplePopUpModalType,
        title: string | undefined | null = '',
        msg: string | undefined | null = '',
        buttonOptions?: Array<ISimplePopUpBtnOption<T>>,
        modalSize: SimplePopUpSize = SimplePopUpSize.lg,
        preventClose: boolean = false,
        didOpen?: (arg?: any) => void,
        timerSec?: number,
        preventTimeoutClose: boolean = false
    ): Promise<any> {
        let icon = '';
        let text_color = 'text-primary';
        if (type === SimplePopUpModalType.Warning) {
            icon = `<img class="max-86 d-block m-auto" src='data:image/svg+xml;base64,PHN2ZyBpZD0iYTNmOWQwYzQtNjA3NC00N2FlLTgwOWMtYmY2NmQwM2RkNmU2IiBkYXRhLW5hbWU9ImdyaWQiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgdmlld0JveD0iMCAwIDE5MiAxOTIiPjxjaXJjbGUgY3g9Ijk2IiBjeT0iOTYiIHI9IjY2Ljg4IiBzdHlsZT0iZmlsbDpub25lO3N0cm9rZTojZWY3ODAwO3N0cm9rZS1saW5lY2FwOnJvdW5kO3N0cm9rZS1saW5lam9pbjpyb3VuZDtzdHJva2Utd2lkdGg6OHB4Ii8+PGxpbmUgeDE9Ijk2IiB5MT0iNTIiIHgyPSI5NiIgeTI9IjExNiIgc3R5bGU9ImZpbGw6bm9uZTtzdHJva2U6I2VmNzgwMDtzdHJva2UtbGluZWNhcDpyb3VuZDtzdHJva2UtbGluZWpvaW46cm91bmQ7c3Ryb2tlLXdpZHRoOjhweCIvPjxsaW5lIHgxPSI5Ni4wNiIgeTE9IjEzNS41IiB4Mj0iOTYuMDYiIHkyPSIxNDAiIHN0eWxlPSJmaWxsOm5vbmU7c3Ryb2tlOiNlZjc4MDA7c3Ryb2tlLWxpbmVjYXA6cm91bmQ7c3Ryb2tlLWxpbmVqb2luOnJvdW5kO3N0cm9rZS13aWR0aDo4cHgiLz48L3N2Zz4='/>`;
            text_color = 'text-orange';
        } else if (type === SimplePopUpModalType.Success) {
            icon = '';
        }

        const templete = `<p class="p1 py-3 ${text_color} text-center">${icon}${title}</p>
        <p class="p2 mb-4 text-center" style="white-space: pre-line;">${msg}</p>`;
        return this.show(
            templete,
            buttonOptions,
            modalSize,
            preventClose,
            didOpen,
            timerSec,
            preventTimeoutClose
        );
    }

    show<T>(
        html: string | undefined | null = '',
        buttonOptions?: Array<ISimplePopUpBtnOption<T>>,
        modalSize: SimplePopUpSize = SimplePopUpSize.lg,
        preventClose: boolean = false,
        didOpen?: (arg?: any) => void,
        timerSec?: number,
        preventTimeoutClose: boolean = false
    ) {
        const popUpHtml: HTMLElement | null = document.getElementById('SimplePopupHtmlOuter');
        if (popUpHtml) {
            popUpHtml.remove();
        }
        const [prompt, resolve] = this.thread();

        let buttonHtml = '';
        if (typeof buttonOptions !== 'undefined') {
            buttonOptions.forEach((element) => {
                if (element.type === 'callback') {
                    if (element.btnDisabled) {
                        buttonHtml += `<button disabled id="${element.id}" class="btn ${element.btnClass}">${element.title}</button>`;
                    } else {
                        buttonHtml += `<button id="${element.id}" class="btn ${element.btnClass}">${element.title}</button>`;
                    }
                } else if (element.type === 'href') {
                    buttonHtml += `<a id="${element.id}" class="btn ${element.btnClass}" href="${element.href}">${element.title}</a>`;
                } else if (element.type === 'href_blank') {
                    buttonHtml += `<a id="${element.id}" class="btn ${element.btnClass}" href="${element.href}" target="_blank">${element.title}</a>`;
                }
            });
        }

        const hideEvent = this.hide;
        const _newPopupHtmlOuter = `<div id="SimplePopupHtmlOuter" class="overlay-bg" style="display: block;">
            <div id="SimplePopupHtmlOverlay"  class="overlay"></div>
            <div class="SimplePopup ${modalSize}" id="popup1">
                <a id="closeBtn" class="cross text-secondary">
                <img src='data:image/svg+xml;utf8, <svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" fill="currentColor" class="bi bi-x-lg" viewBox="0 0 16 16">
                <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8z"/>
                </svg>' />
                </a>
                <div id="contentWrapper" class="mt-6 contentWrapper">
                ${html}
                </div>
                
                <div id="SimplePopupBtnOuter" class="d-flex justify-content-center pt-2">
                    ${buttonHtml}
                </div>
        </div>`;
        const newPopupHtmlOuter = DOMPurify.sanitize(_newPopupHtmlOuter);
        document.body.insertAdjacentHTML('beforeend', newPopupHtmlOuter);

        if (timerSec) {
            clearInterval(this.timerInterval);
            setTimeout(() => {
                const b = document.querySelector('.timer') || null;
                if (b) {
                    let timer = timerSec !== undefined ? timerSec * 1000 : 30 * 1000;

                    // b.textContent = b.textContent = Common.GetLeftTimeText(timer);
                    this.timerInterval = window.setInterval(() => {
                        if (timer <= 0) {
                            b.textContent = Common.GetLeftTimeText(timer);
                            if (this.timerInterval) {
                                clearInterval(this.timerInterval);
                                if (preventTimeoutClose) {
                                    resolve('timeout');
                                } else {
                                    hideEvent();
                                    resolve('hide');
                                }
                            }
                        } else {
                            b.textContent = Common.GetLeftTimeText(timer);
                            timer -= 1000;
                        }
                    }, 1000);
                }
            });
        }

        /**
         * did open
         */
        if (typeof buttonOptions !== 'undefined') {
            buttonOptions.forEach((element) => {
                if (
                    element.type === 'callback' ||
                    element.type === 'href' ||
                    element.type === 'href_blank'
                ) {
                    const _btnElement: HTMLElement | null = document.getElementById(element.id);
                    if (_btnElement) {
                        _btnElement.addEventListener('click', (event) => {
                            hideEvent();
                            resolve(element.resolveParam, event);
                        });
                    }
                }
            });
        }
        if (typeof didOpen !== 'undefined') {
            didOpen();
        }
        /**
         * will close
         */
        const SimplePopupHtmlOverlay: HTMLElement | null =
            document.getElementById('SimplePopupHtmlOverlay');
        if (SimplePopupHtmlOverlay && preventClose === false) {
            SimplePopupHtmlOverlay.addEventListener('click', (event) => {
                hideEvent();
                resolve('hide', event);
            });
        }
        document.body.style.overflow = 'hidden';
        const closeBtn: HTMLElement | null = document.getElementById('closeBtn');
        if (closeBtn) {
            closeBtn.addEventListener('click', (event) => {
                hideEvent();
                resolve('hide', event);
            });
        }
        return prompt;
    }

    /**
     * will close
     */
    hide() {
        document.body.style.overflow = 'auto';
        const popUpHtml: HTMLElement | null = document.getElementById('SimplePopupHtmlOuter');
        if (popUpHtml) {
            let opacity = 1;
            const interval = setInterval(function () {
                if (opacity > 0) {
                    opacity -= 0.1;
                    popUpHtml.style.opacity = opacity.toString();
                } else {
                    clearInterval(interval);
                    popUpHtml.style.display = 'none';
                    popUpHtml.remove();
                }
            }, 10);
        }
    }
}
